/**
 * D-Tools SI Location hierarchy helpers.
 * Child rooms use ParentId + short Name + FullName ("Parent: Child").
 * Portal display prefers FullName so child rooms stay distinct.
 */

export type DtoolsSiLocation = {
  id: string;
  /** Short room label from D-Tools `Name` (e.g. "Bathroom"). */
  name: string;
  /** Hierarchical label from D-Tools `FullName` (e.g. "Master Suite: Bathroom"). */
  fullName: string;
  /** Parent location Id when this is a sub-room; null for top-level rooms. */
  parentId: string | null;
};

function asRecord(v: unknown): Record<string, unknown> | null {
  return v !== null && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function str(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v).trim();
  return s.length ? s : null;
}

/** Display label: FullName when present, otherwise Name. Flat rooms keep Name === FullName. */
export function dtoolsLocationDisplayName(loc: Pick<DtoolsSiLocation, "name" | "fullName">): string {
  const full = String(loc.fullName ?? "").trim();
  if (full) return full;
  return String(loc.name ?? "").trim();
}

/**
 * Resolve a hierarchical room label from persisted line fields.
 * Prefers locationFullName, then roomName (already FullName on new imports), then short locationName.
 */
export function formatDtoolsHierarchicalRoomLabel(input: {
  roomName?: string | null;
  locationFullName?: string | null;
  locationName?: string | null;
}): string {
  const full = String(input.locationFullName ?? "").trim();
  if (full) return full;
  const room = String(input.roomName ?? "").trim();
  if (room) return room;
  const shortName = String(input.locationName ?? "").trim();
  return shortName || "No room";
}

/** Parse D-Tools Locations[] preserving Id / ParentId / Name / FullName. */
export function parseDtoolsSiLocations(rawLocations: unknown): DtoolsSiLocation[] {
  if (!Array.isArray(rawLocations)) return [];
  const out: DtoolsSiLocation[] = [];
  const seen = new Set<string>();
  for (const row of rawLocations) {
    const o = asRecord(row);
    if (!o) continue;
    const id = str(o.Id ?? o.id);
    if (!id || seen.has(id)) continue;
    const name = str(o.Name ?? o.name) ?? "";
    const fullName = str(o.FullName ?? o.fullName) ?? name;
    if (!name && !fullName) continue;
    const parentId = str(o.ParentId ?? o.parentId);
    seen.add(id);
    out.push({
      id,
      name: name || fullName,
      fullName: fullName || name,
      parentId,
    });
  }
  return out;
}

export function dtoolsLocationByIdMap(
  locations: DtoolsSiLocation[],
): Map<string, DtoolsSiLocation> {
  return new Map(locations.map((l) => [l.id, l]));
}
